import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { splitName } from '@/modules/service-requests/shared/application-utils';
import { useEmployeeSnapshot } from '@/modules/service-requests/shared/employee';
import { translatedResolver } from '@/modules/service-requests/shared/form-resolver';
import { ApplyFormPage } from '@/modules/service-requests/shared/components/apply-form-page';
import {
  FieldGrid,
  PhoneField,
  SelectField,
  TextField,
} from '@/modules/service-requests/shared/components/form-fields';
import { useIdCardApplication, useIdCardFileUrls, useSubmitIdCard } from '../../hooks/use-id-card';
import {
  BLOOD_GROUPS,
  ID_CARD_APPLICATION_TYPES,
  PAYMENT_METHODS,
  requiresPayment,
} from '../../utils/id-card-rules';
import { IdCardApplyValues, idCardApplySchema } from '../../schemas/id-card-apply-form-schema';
import { ImageUploadField } from '../image-upload-field/image-upload-field';
import { appPath, SERVICE_REQUEST_TYPES } from '@/constant/app-paths';

const EMPTY_VALUES: IdCardApplyValues = {
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
  bloodGroup: '',
  designation: '',
  employeeId: '',
  applicationType: 'Reissue',
  paymentMethod: '',
  paymentReference: '',
};

export const IdCardApplyForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const applicationId = searchParams.get('id') ?? undefined;

  const { snapshot, isLoading: isSnapshotLoading } = useEmployeeSnapshot();
  const { data: existing, isLoading: isExistingLoading } = useIdCardApplication(applicationId);
  // The saved photo and receipt links: the previews wait for them instead of showing nothing.
  const { data: existingUrls = {}, isLoading: isUrlsLoading } = useIdCardFileUrls(
    existing ? [existing] : []
  );
  const { mutateAsync: submit, isPending } = useSubmitIdCard();

  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [keepExistingPhoto, setKeepExistingPhoto] = useState(true);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [keepExistingReceipt, setKeepExistingReceipt] = useState(true);
  const [photoMissing, setPhotoMissing] = useState(false);

  const form = useForm<IdCardApplyValues>({
    resolver: translatedResolver(idCardApplySchema, t),
    defaultValues: EMPTY_VALUES,
  });
  const applicationType = form.watch('applicationType');
  const isReissue = requiresPayment(applicationType);

  useEffect(() => {
    if (applicationId) {
      if (!existing) return;
      const { firstName, lastName } = splitName(existing.Name);
      form.reset({
        firstName,
        lastName,
        email: existing.Email ?? '',
        mobileNumber: existing.MobileNumber ?? '',
        bloodGroup: existing.BloodGroup ?? '',
        designation: existing.Designation ?? '',
        employeeId: existing.EmployeeId ?? '',
        applicationType: existing.ApplicationType ?? 'Reissue',
        paymentMethod: existing.PaymentMethod ?? '',
        paymentReference: existing.PaymentReference ?? '',
      });
      return;
    }
    if (!snapshot) return;
    const { firstName, lastName } = splitName(snapshot.displayName);
    form.reset({
      ...EMPTY_VALUES,
      firstName,
      lastName,
      email: snapshot.email,
      mobileNumber: snapshot.phoneNumber,
      bloodGroup: snapshot.bloodGroup,
      designation: snapshot.designation,
      employeeId: snapshot.employeeId,
    });
  }, [applicationId, existing, snapshot, form]);

  // Switching to New Joiner drops the payment section, exactly as the legacy form did.
  useEffect(() => {
    if (isReissue) return;
    form.setValue('paymentMethod', '');
    form.setValue('paymentReference', '');
    setReceiptFile(null);
    setKeepExistingReceipt(false);
  }, [isReissue, form]);

  const photoUrl =
    existing?.PhotoId && keepExistingPhoto ? existingUrls[existing.PhotoId] : undefined;
  const receiptUrl =
    existing?.PaymentReferenceId && keepExistingReceipt
      ? existingUrls[existing.PaymentReferenceId]
      : undefined;
  const hasPhoto = Boolean(photoFile) || Boolean(existing?.PhotoId && keepExistingPhoto);

  const goBack = () => navigate(appPath.serviceRequests(SERVICE_REQUEST_TYPES.ID_CARD));

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!hasPhoto) {
      setPhotoMissing(true);
      return;
    }
    try {
      await submit({
        existing: existing ?? undefined,
        values,
        photoFile,
        keepExistingPhoto,
        receiptFile,
        keepExistingReceipt,
      });
      goBack();
    } catch {
      // Error toast is already shown by the mutation's global error handler.
    }
  });

  return (
    <ApplyFormPage
      form={form}
      titleKey={applicationId ? 'EDIT_ID_CARD_APPLICATION' : 'ID_CARD_APPLICATION_FORM'}
      isSubmitting={isPending}
      isEdit={Boolean(applicationId)}
      isLoading={applicationId ? isExistingLoading || isUrlsLoading : isSnapshotLoading}
      // A new application needs the applicant's profile (prefill and department); an edit does not.
      submitDisabled={!applicationId && !snapshot}
      onCancel={goBack}
      onSubmit={handleSubmit}
    >
      <div className="flex flex-col gap-6">
        <FieldGrid>
          <TextField control={form.control} name="firstName" required label={t('FIRST_NAME')} />
          <TextField control={form.control} name="lastName" required label={t('LAST_NAME')} />
          <TextField control={form.control} name="email" required type="email" label={t('EMAIL')} />
          <PhoneField
            control={form.control}
            name="mobileNumber"
            required
            label={t('MOBILE_NUMBER')}
          />
          <SelectField
            control={form.control}
            name="bloodGroup"
            required
            label={t('BLOOD_GROUP')}
            options={BLOOD_GROUPS.map((group) => ({ value: group, label: group }))}
          />
          <TextField control={form.control} name="designation" required label={t('DESIGNATION')} />
          <TextField control={form.control} name="employeeId" required label={t('EMPLOYEE_ID')} />
          <SelectField
            control={form.control}
            name="applicationType"
            required
            label={t('APPLICATION_TYPE')}
            options={ID_CARD_APPLICATION_TYPES.map((type) => ({
              value: type.value,
              label: t(type.labelKey),
            }))}
          />
        </FieldGrid>

        <ImageUploadField
          label={t('UPLOAD_PHOTO')}
          required
          promptKey="CLICK_TO_SELECT_PHOTO"
          hint={t('MAX_IMAGE_SIZE_HINT')}
          file={photoFile}
          existingUrl={photoUrl}
          existingLabelKey="CURRENT_PHOTO"
          onFileChange={(file) => {
            setPhotoFile(file);
            setPhotoMissing(false);
          }}
          onRemoveExisting={() => setKeepExistingPhoto(false)}
          error={photoMissing ? t('PHOTO_IS_REQUIRED') : undefined}
        />

        {isReissue && (
          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold">{t('PAYMENT_INFO')}</h2>
            <FieldGrid>
              <SelectField
                control={form.control}
                name="paymentMethod"
                required
                label={t('PAYMENT_METHOD')}
                placeholder={t('SELECT_PAYMENT_METHOD')}
                options={PAYMENT_METHODS.map((method) => ({ value: method, label: method }))}
              />
              <TextField
                control={form.control}
                name="paymentReference"
                label={t('PAYMENT_REFERENCE')}
                placeholder={t('TYPE_PAYMENT_REFERENCE')}
              />
            </FieldGrid>
            <ImageUploadField
              label={t('UPLOAD_PAYMENT_RECEIPT')}
              promptKey="CLICK_TO_SELECT_RECEIPT"
              hint={t('MAX_IMAGE_SIZE_HINT')}
              file={receiptFile}
              existingUrl={receiptUrl}
              existingLabelKey="UPLOADED_PAYMENT_RECEIPT"
              onFileChange={setReceiptFile}
              onRemoveExisting={() => setKeepExistingReceipt(false)}
            />
          </section>
        )}
      </div>
    </ApplyFormPage>
  );
};
